/**
 * A screen that only exists for a demo, hidden unless a presenter asks for it.
 *
 * CONFIDENTIAL — ASI Internal.
 *
 * The problem it solves is specific: `/agent/mmm/output` renders a seeded worked
 * example, and the MMM agent's own dashboard renders a real delivery. Both are
 * reachable from the same module, both are full of numbers, and only one of them
 * means anything. A reader cannot be expected to hold that distinction (DEC-49).
 *
 * The gate is on the ROUTE rather than inside the page, so it holds however
 * somebody arrives — a rail link, a saved analysis deep link, a pasted URL. A
 * check inside the page would be one `return` away from being bypassed by the
 * next deep link somebody adds.
 *
 * It refuses by explaining, not by 404-ing. Someone who typed this URL was
 * looking for something, and "turn it on in Settings" is a more useful answer
 * than "does not exist" — which would also be a lie.
 */

import type { JSX, ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { PATHS, settingsSection } from '@/routes/paths';
import { useTweaksStore } from '@/state/tweaksStore';
import { Button, Card, Notice } from '@/components/primitives';

export function DemoOnly({ children }: { readonly children: ReactNode }): JSX.Element {
  const enabled = useTweaksStore((s) => s.showWorkedOutputs);
  const navigate = useNavigate();

  if (enabled) return <>{children}</>;

  return (
    <Card>
      <Notice tone="warn" icon="alert">
        <b>This screen is a worked example.</b> Its figures are illustrative — they describe a
        made-up engagement, not yours — so it is off unless somebody turns it on for a
        demonstration. The agent&rsquo;s own dashboard shows the real delivery.
      </Notice>
      <div className="row">
        <Button
          variant="primary"
          icon="settings"
          onClick={() => {
            void navigate(settingsSection('session'));
          }}
        >
          Turn it on in Settings
        </Button>
        <Button
          variant="secondary"
          icon="bot"
          onClick={() => {
            void navigate(PATHS.agentMmm);
          }}
        >
          Go to the agent
        </Button>
      </div>
    </Card>
  );
}
